"use client";

import { useAtomValue } from "jotai";
import { KeyRound, TriangleAlert } from "lucide-react";

import { DataTypeBadge } from "@/icons/DataTypeIcon";
import { TooltipContent } from "@/components/shadcn/tooltip";
import { activeFlowIdAtom } from "@/store/editor-v2";
import { useSpotUser } from "@/utils/spotUser";
import { getActiveGroupId } from "@/utils/activeGroup";
import { timeAgo } from "@/utils/generic";
import { normalizePreviewIoContainer } from "@/components/Spotflow/WorkflowRunner/Outputs/ioFileDisplay";
import { CompactListPreview } from "@/components/Spotflow/WorkflowRunner/Outputs/ioListPreview";
import { IoValueBody } from "@/components/SpotflowV2/CustomNodes/BaseNode/PreviewIoPanel";
import {
  parseVariableRefSource,
  useLastRunVariableValue,
} from "./useLastRunVariableValue";
import { NoxusIcon } from "@/components/UI/NoxusIcon";

// Mounted only while the tooltip is open (Radix portals its content), so the
// runs/IO lookups fire lazily on hover and get cached by React Query.
function LastRunValueSection({
  sourceNodeId,
  outputName,
  dataType,
  isList,
}: {
  sourceNodeId: string;
  outputName: string;
  dataType: string;
  isList?: boolean;
}) {
  const { spotGroup } = useSpotUser();
  const activeFlowId = useAtomValue(activeFlowIdAtom);
  // Isolated Milkdown/CodeMirror chip roots sit outside SpotUserProvider;
  // fall back to the session workspace so last-run still resolves on hover.
  const groupId = spotGroup?.id || getActiveGroupId() || undefined;
  const { data, isLoading } = useLastRunVariableValue({
    groupId,
    workflowId: activeFlowId ?? undefined,
    sourceNodeId,
    outputName,
  });

  if (!groupId || !activeFlowId) return null;

  return (
    <div className="border-t border-border/60 px-3.5 py-2.5">
      <div className="flex items-center justify-between gap-3">
        <span className="text-[11px] text-muted-foreground">
          Last run value
        </span>
        {data && (
          <span className="shrink-0 text-[10px] text-muted-foreground/70">
            {timeAgo(data.runTimestamp)}
          </span>
        )}
      </div>
      <div className="mt-1 min-w-0">
        {isLoading ? (
          <p className="animate-pulse text-[11px] text-muted-foreground/70">
            Loading…
          </p>
        ) : data ? (
          <LastRunValuePreview
            value={data.value}
            dataType={dataType}
            isList={isList}
          />
        ) : (
          <p className="text-[11px] italic text-muted-foreground/70">
            No value in recent runs
          </p>
        )}
      </div>
    </div>
  );
}

// Same normalization + renderers as the under-node preview panel (IoEntry):
// arrays get the 2-item CompactListPreview, everything else the compact
// IoValueBody — so the tooltip's value reads identically to the pane.
function LastRunValuePreview({
  value,
  dataType,
  isList,
}: {
  value: unknown;
  dataType: string;
  isList?: boolean;
}) {
  const container = normalizePreviewIoContainer(value, !!isList);
  if (Array.isArray(container.value)) {
    return (
      <CompactListPreview
        items={container.value}
        dataType={dataType}
        maxVisible={2}
      />
    );
  }
  return (
    <div className="max-h-[160px] overflow-y-auto">
      <IoValueBody
        container={{ type: container.type ?? dataType, value: container.value }}
        compact
      />
    </div>
  );
}

export function BoundVariableTooltipContent({
  variableLabel,
  dataType,
  isList,
  sourceLabel,
  sourceImage,
  sourceIconName,
  sourceColor,
  variableRef,
  isSecret,
  warning,
}: {
  variableLabel: string;
  dataType: string;
  isList?: boolean;
  sourceLabel: string;
  sourceImage?: string | null;
  sourceIconName?: string | null;
  sourceColor?: string;
  /** Whole `<sourceNodeId>.<outputName>` ref — when given (and not a secret),
   * the tooltip also shows the variable's value from the last run that has it. */
  variableRef?: string | null;
  isSecret?: boolean;
  warning?: string;
}) {
  const refSource = isSecret ? null : parseVariableRefSource(variableRef);

  return (
    <TooltipContent
      side="top"
      sideOffset={8}
      hideArrow
      className="bg-popover text-popover-foreground border border-border shadow-md rounded-md p-0 min-w-[200px] max-w-[320px] overflow-hidden"
    >
      <div className="border-b border-border px-3 py-2">
        <p className="text-xs font-medium leading-tight break-words">
          {variableLabel}
        </p>
        {isSecret ? (
          <p className="text-xs text-muted-foreground mt-0.5">
            Workspace secret: value stays hidden
          </p>
        ) : (
          isList && (
            <p className="text-xs text-muted-foreground mt-0.5">
              List of values
            </p>
          )
        )}
      </div>
      <div className="flex flex-col gap-1.5 px-3 py-2">
        <div className="flex items-center justify-between gap-6">
          <span className="text-xs text-muted-foreground">Type</span>
          <DataTypeBadge
            dataType={dataType}
            isList={isList}
            className="text-xs text-primary gap-1.5"
          />
        </div>
        <div className="flex items-center justify-between gap-6">
          <span className="text-xs text-muted-foreground">Source</span>
          <div className="flex min-w-0 items-center gap-1.5">
            {sourceIconName ? (
              <NoxusIcon icon={sourceIconName} size="xsm" />
            ) : (
              <div
                className="size-4 rounded flex items-center justify-center shrink-0"
                style={{
                  backgroundColor: sourceColor ?? "#f4f4f5",
                }}
              >
                {isSecret ? (
                  <KeyRound className="size-2.5 text-indigo-600" />
                ) : sourceImage ? (
                  <img
                    src={sourceImage}
                    alt=""
                    className="size-2.5 object-contain"
                  />
                ) : (
                  <div className="size-1.5 rounded-sm bg-foreground/20" />
                )}
              </div>
            )}
            <span className="min-w-0 truncate text-xs text-foreground">
              {sourceLabel}
            </span>
          </div>
        </div>
      </div>
      {warning && (
        <div className="flex items-start gap-1.5 border-t border-warning-200 bg-warning-50 px-3 py-2">
          <TriangleAlert className="mt-px size-3 shrink-0 text-warning-600" />
          <p className="text-xs leading-snug text-warning-700">{warning}</p>
        </div>
      )}
      {refSource && (
        <LastRunValueSection
          sourceNodeId={refSource.sourceNodeId}
          outputName={refSource.outputName}
          dataType={dataType}
          isList={isList}
        />
      )}
    </TooltipContent>
  );
}
